/**
 * shared.ts — small helpers reused across the screen Web Components.
 * =====================================================================
 * No framework, no extra deps. Everything here is presentation glue around the
 * typed `client.ts` wrappers and the `bind.ts` convention engine. None of it
 * invents API fields — it only formats/maps the contract's existing values.
 */
import type { WorkOrder, ProviderStatus } from "../api/client";

/** Fetch a hand-authored view fragment (a same-origin static asset). */
export async function loadView(url: string): Promise<string> {
  return fetch(url, { credentials: "same-origin" }).then((r) => r.text());
}

/**
 * The prototype's pipeline order. Source: mango_portal_complete.html statusOrder
 * (with the contract's underscore enum, not the prototype's `mango-reviewing`).
 */
export const PIPELINE: ProviderStatus[] = [
  "offered",
  "scheduled",
  "onsite",
  "awaiting",
  "finalizing",
  "mango_reviewing",
];

/** Human stage labels (mirrors the prototype statusConfig labels). */
export const STAGE_LABEL: Record<ProviderStatus, string> = {
  offered: "Work order offered",
  scheduled: "Scheduled work order",
  onsite: "Onsite now",
  awaiting: "Equipment Processing",
  finalizing: "FMV Approval",
  mango_reviewing: "Mango reviewing",
  complete: "Complete",
};

/**
 * Stages that "need attention" (the prototype's actionableAdmin set). Used by the
 * jobs toggle. This is purely presentational grouping over provider_status.
 */
export const ATTENTION: ProviderStatus[] = ["offered", "onsite", "awaiting", "finalizing"];

/**
 * Per-status visual config — lifted VERBATIM from the prototype's `statusConfig`
 * (mango_portal_complete.html:1933) + `getCTA` (:2148). This is pure presentation
 * (Tabler glyph, icon background/foreground colour, status-pill class, card CTA
 * label) that the prototype kept in its demo JS; in the portal it belongs here in
 * the presentation glue so views/jobs.html can stay declarative. No API fields.
 */
export interface StatusVisual {
  icon: string; // Tabler glyph class, e.g. "ti-tag"
  bg: string; // CSS var for the icon chip background
  color: string; // CSS var for the icon glyph colour
  pillClass: string; // status-pill class, e.g. "pill-purple"
  cta: string; // card call-to-action label
}
export const STATUS_CONFIG: Record<ProviderStatus, StatusVisual> = {
  offered: { icon: "ti-tag", bg: "var(--purple-light)", color: "var(--purple)", pillClass: "pill-purple", cta: "Review offer" },
  scheduled: { icon: "ti-calendar", bg: "var(--accent-light)", color: "var(--accent)", pillClass: "pill-accent", cta: "View brief" },
  onsite: { icon: "ti-map-pin", bg: "var(--amber-light)", color: "var(--amber)", pillClass: "pill-amber", cta: "View brief" },
  awaiting: { icon: "ti-clock", bg: "var(--red-light)", color: "var(--red)", pillClass: "pill-red", cta: "Upload docs" },
  finalizing: { icon: "ti-check", bg: "var(--purple-light)", color: "var(--purple)", pillClass: "pill-purple", cta: "Finalize FMV" },
  mango_reviewing: { icon: "ti-eye", bg: "var(--bg)", color: "var(--text-2)", pillClass: "pill-gray", cta: "View" },
  complete: { icon: "ti-circle-check", bg: "var(--green-light)", color: "var(--green)", pillClass: "pill-green", cta: "View" },
};

/**
 * The stage-appropriate detail route for a work order (mirrors the prototype's
 * openJob() switch: offered → detail; scheduled → brief; onsite → onsite;
 * awaiting → sow; finalizing → finalize; everything else → detail).
 */
export function detailRoute(job: WorkOrder): string {
  switch (job.provider_status) {
    case "scheduled":
      return "brief";
    case "onsite":
      // Onsite WOs open the brief in its "arrived" state (Arrived at … + Start
      // onsite scope); the onsite-scope form is reached from there.
      return "brief";
    case "awaiting":
      return "sow";
    case "finalizing":
      return "finalize";
    default:
      return "detail";
  }
}

/**
 * ROLE-GATING IS COSMETIC (the server enforces). A Provider-Staff response omits
 * `sow_rate` (and the settlement); a Provider-Admin response includes it. We gate
 * on FIELD PRESENCE, never a client-side role flag: if `sow_rate` is absent the
 * financial UI is hidden. `null` is "present but unset" (Admin, not yet stamped).
 */
export function hasFinancials(job: WorkOrder): boolean {
  return "sow_rate" in job && (job as Record<string, unknown>).sow_rate !== undefined;
}

/** Format a contract decimal/string money value for display (no currency math). */
export function money(value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Format an ISO date or date-time for display; passthrough on parse failure.
 * Date-only strings (YYYY-MM-DD) are parsed with the LOCAL constructor to avoid
 * UTC-midnight → previous-day off-by-one in US timezones. Full datetime strings
 * (containing "T") use the standard Date constructor and keep their embedded
 * timezone/offset. Returns "—" for null/empty, original value if unparseable.
 */
export function date(value: string | null | undefined): string {
  if (!value) return "—";
  const trimmed = value.trim();
  let d: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const parts = trimmed.split("-");
    d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  } else {
    d = new Date(trimmed);
  }
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** Format the time-of-day from an ISO timestamp, e.g. "4:13 PM". "—" if absent. */
export function timeOfDay(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/**
 * Compact "time remaining until <iso>" label, e.g. "2d 4h", "5h", "30m". Returns
 * null if the timestamp is missing, unparseable, or already in the past — callers
 * hide the chip in that case (we never show a fabricated or negative countdown).
 */
export function timeLeft(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const ms = target - Date.now();
  if (ms <= 0) return null;
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
}

/**
 * SLA countdown chip from `sla_due_at` (the work order's processing deadline =
 * onsite_completed_at + 10 business days; null until onsite is complete). Returns
 * the chip label + urgency class (mirrors the prototype getClockChip thresholds),
 * or null when there is no deadline yet (the caller keeps the chip hidden — we
 * never show a fabricated countdown).
 */
export function slaChip(slaDueAt: string | null | undefined): { label: string; cls: string } | null {
  if (!slaDueAt) return null;
  const due = new Date(slaDueAt).getTime();
  if (Number.isNaN(due)) return null;
  const ms = due - Date.now();
  if (ms <= 0) return { label: "OVERDUE", cls: "urgent" };
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  const cls = days > 5 ? "safe" : days > 2 ? "warning" : "urgent";
  return { label: `${days}d ${hours}h left`, cls };
}

/**
 * Pluralized stage count label for the section badge, e.g. "1 offer" / "3 offers",
 * "2 scheduled" / "1 onsite". Keeps stage labels short and count-aware so the
 * section badge reads like natural language rather than a bare number.
 */
export function stagePluralLabel(status: ProviderStatus, count: number): string {
  const singular: Record<ProviderStatus, string> = {
    offered:        "offer",
    scheduled:      "scheduled",
    onsite:         "onsite now",
    awaiting:       "processing",
    finalizing:     "finalizing",
    mango_reviewing:"in review",
    complete:       "complete",
  };
  const plural: Record<ProviderStatus, string> = {
    offered:        "offers",
    scheduled:      "scheduled",
    onsite:         "onsite now",
    awaiting:       "processing",
    finalizing:     "finalizing",
    mango_reviewing:"in review",
    complete:       "complete",
  };
  const word = count === 1 ? singular[status] : plural[status];
  return `${count} ${word}`;
}

/** Set the text of an element matched by selector under root, if present. */
export function setText(root: ParentNode, selector: string, text: string): void {
  const el = root.querySelector<HTMLElement>(selector);
  if (el) el.textContent = text;
}

/** Show/hide an element matched by selector (display toggle, keeps it in the DOM). */
export function toggle(root: ParentNode, selector: string, show: boolean): void {
  const el = root.querySelector<HTMLElement>(selector);
  if (!el) return;
  el.style.display = show ? "" : "none";
  // Also clear/set the `hidden` attribute: elements authored as `<… hidden>`
  // (e.g. completed.html's `<table … hidden>`) keep the UA `[hidden]{display:none}`
  // rule, which outranks an inline `display:""` and would stay invisible.
  el.toggleAttribute("hidden", !show);
}
